import * as z from 'zod';

export const ListRsvpRequest = z.object({
  options: z
    .object({
      offset: z
        .number()
        .int()
        .describe(
          'Number of items to skip. See [Pagination](https://dev.wix.com/api/rest/getting-started/pagination).'
        )
        .min(0)
        .optional(),
      limit: z
        .number()
        .int()
        .describe(
          'Number of items to load. See [Pagination](https://dev.wix.com/api/rest/getting-started/pagination).'
        )
        .min(0)
        .max(400)
        .optional(),
      fieldset: z
        .array(z.enum(['DETAILS', 'FORM', 'CONTACT_DETAILS']))
        .max(20)
        .optional(),
      eventId: z.array(z.string()).max(100).optional(),
      rsvpId: z.array(z.string()).max(500).optional(),
      status: z
        .array(z.enum(['YES', 'NO', 'WAITING']))
        .max(20)
        .optional(),
      memberId: z.array(z.string()).max(500).optional(),
      facet: z.array(z.string()).max(20).optional(),
      searchPhrase: z
        .string()
        .describe(
          'Textual search filter - search is performed on "full_name" and "email".'
        )
        .max(200)
        .optional(),
      eventCreatorId: z.array(z.string()).max(50).optional(),
      sort: z
        .string()
        .describe('Sort order, defaults to `"created:asc"`.')
        .max(100)
        .optional(),
      contactId: z.array(z.string()).max(100).optional(),
      tag: z
        .array(z.enum(['FULLY_CHECKED_IN', 'NOT_FULLY_CHECKED_IN', 'MEMBER']))
        .optional(),
    })
    .describe('Optional fields.')
    .optional(),
});
export const ListRsvpResponse = z.object({
  total: z
    .number()
    .int()
    .describe('Total RSVPs matching the given filters.')
    .optional(),
  offset: z.number().int().describe('Offset.').min(0).optional(),
  limit: z.number().int().describe('Limit.').min(0).max(100).optional(),
  rsvps: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('RSVP ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        eventId: z
          .string()
          .describe('Event ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        contactId: z
          .string()
          .describe('Contact ID associated with this RSVP.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        memberId: z
          .string()
          .describe('Member ID associated with this RSVP.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        created: z
          .date()
          .describe('RSVP created timestamp.')
          .optional()
          .nullable(),
        modified: z
          .date()
          .describe('RSVP modified timestamp.')
          .optional()
          .nullable(),
        firstName: z.string().describe('First name.').optional(),
        lastName: z.string().describe('Last name.').optional(),
        email: z.string().describe('Guest email.').email().optional(),
        rsvpForm: z
          .object({
            inputValues: z
              .array(
                z.object({
                  inputName: z
                    .string()
                    .describe('Form field input name.')
                    .max(100)
                    .optional(),
                  value: z
                    .string()
                    .describe('Form field value.')
                    .max(5000)
                    .optional(),
                  values: z.array(z.string()).max(100).optional(),
                })
              )
              .max(200)
              .optional(),
          })
          .describe('RSVP form response.')
          .optional(),
        status: z
          .enum(['YES', 'NO', 'WAITING'])
          .describe('RSVP response status.')
          .optional(),
        totalGuests: z
          .number()
          .int()
          .describe('Total number of attendees.')
          .optional(),
        guests: z
          .array(
            z.object({
              index: z
                .number()
                .int()
                .describe('Index in the RSVP guest list.')
                .optional(),
              fullName: z.string().describe('Guest full name.').optional(),
              checkIn: z
                .object({
                  created: z
                    .date()
                    .describe("Time of a ticket's check-in.")
                    .optional()
                    .nullable(),
                })
                .describe('Guest check-in.')
                .optional(),
              _id: z
                .number()
                .int()
                .describe('Unique guest ID per RSVP.')
                .min(1)
                .optional(),
            })
          )
          .optional(),
        anonymized: z
          .boolean()
          .describe('Whether RSVP is anonymized by GDPR delete.')
          .optional(),
        marketingConsent: z
          .boolean()
          .describe('Whether marketing consent was given')
          .optional()
          .nullable(),
      })
    )
    .optional(),
  facets: z
    .record(
      z.string(),
      z.object({
        counts: z
          .record(z.string(), z.number().int())
          .describe('Facet counts aggregated per value.')
          .optional(),
      })
    )
    .describe('Facet query result.')
    .optional(),
  rsvpFacets: z
    .object({
      facets: z
        .record(
          z.string(),
          z.object({
            counts: z
              .record(
                z.string(),
                z.object({
                  count: z
                    .number()
                    .int()
                    .describe('Number of RSVPs.')
                    .optional(),
                  guests: z
                    .number()
                    .int()
                    .describe('Number of guests within RSVPs.')
                    .optional(),
                  guestsCheckIn: z
                    .number()
                    .int()
                    .describe('Number of guests who have checked-in.')
                    .optional(),
                })
              )
              .describe('Facet totals, aggregated per filter.')
              .optional(),
          })
        )
        .describe('Filter facets.')
        .optional(),
    })
    .describe('Rsvp data enriched facets.')
    .optional(),
});
export const QueryRsvpRequest = z.object({
  options: z
    .object({
      offset: z
        .number()
        .int()
        .describe(
          'Offset. See [Pagination](https://dev.wix.com/api/rest/getting-started/pagination).'
        )
        .min(0)
        .optional(),
      limit: z
        .number()
        .int()
        .describe(
          'Limit. See [Pagination](https://dev.wix.com/api/rest/getting-started/pagination).'
        )
        .min(0)
        .max(100)
        .optional(),
      fieldset: z
        .array(z.enum(['DETAILS', 'FORM', 'CONTACT_DETAILS']))
        .max(20)
        .optional(),
      filter: z
        .record(z.string(), z.any())
        .describe('Filter.')
        .optional()
        .nullable(),
      memberId: z.array(z.string()).max(500).optional(),
      facet: z.array(z.string()).max(20).optional(),
      searchPhrase: z
        .string()
        .describe(
          'Textual search filter - search is performed on "guests.full_name".'
        )
        .max(200)
        .optional(),
      eventCreatorId: z.array(z.string()).max(50).optional(),
      sort: z
        .string()
        .describe('Sort order, defaults to `"created:asc"`.')
        .max(100)
        .optional(),
      contactId: z.array(z.string()).max(100).optional(),
      tag: z
        .array(z.enum(['FULLY_CHECKED_IN', 'NOT_FULLY_CHECKED_IN', 'MEMBER']))
        .optional(),
    })
    .describe('Optional fields.')
    .optional(),
});
export const QueryRsvpResponse = z.object({
  total: z
    .number()
    .int()
    .describe('Total RSVPs matching the given filters.')
    .optional(),
  offset: z.number().int().describe('Offset.').min(0).optional(),
  limit: z.number().int().describe('Limit.').min(0).max(100).optional(),
  rsvps: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('RSVP ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        eventId: z
          .string()
          .describe('Event ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        contactId: z
          .string()
          .describe('Contact ID associated with this RSVP.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        memberId: z
          .string()
          .describe('Member ID associated with this RSVP.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        created: z
          .date()
          .describe('RSVP created timestamp.')
          .optional()
          .nullable(),
        modified: z
          .date()
          .describe('RSVP modified timestamp.')
          .optional()
          .nullable(),
        firstName: z.string().describe('First name.').optional(),
        lastName: z.string().describe('Last name.').optional(),
        email: z.string().describe('Guest email.').email().optional(),
        rsvpForm: z
          .object({
            inputValues: z
              .array(
                z.object({
                  inputName: z
                    .string()
                    .describe('Form field input name.')
                    .max(100)
                    .optional(),
                  value: z
                    .string()
                    .describe('Form field value.')
                    .max(5000)
                    .optional(),
                  values: z.array(z.string()).max(100).optional(),
                })
              )
              .max(200)
              .optional(),
          })
          .describe('RSVP form response.')
          .optional(),
        status: z
          .enum(['YES', 'NO', 'WAITING'])
          .describe('RSVP response status.')
          .optional(),
        totalGuests: z
          .number()
          .int()
          .describe('Total number of attendees.')
          .optional(),
        guests: z
          .array(
            z.object({
              index: z
                .number()
                .int()
                .describe('Index in the RSVP guest list.')
                .optional(),
              fullName: z.string().describe('Guest full name.').optional(),
              checkIn: z
                .object({
                  created: z
                    .date()
                    .describe("Time of a ticket's check-in.")
                    .optional()
                    .nullable(),
                })
                .describe('Guest check-in.')
                .optional(),
              _id: z
                .number()
                .int()
                .describe('Unique guest ID per RSVP.')
                .min(1)
                .optional(),
            })
          )
          .optional(),
        anonymized: z
          .boolean()
          .describe('Whether RSVP is anonymized by GDPR delete.')
          .optional(),
        marketingConsent: z
          .boolean()
          .describe('Whether marketing consent was given')
          .optional()
          .nullable(),
      })
    )
    .optional(),
  facets: z
    .record(
      z.string(),
      z.object({
        counts: z
          .record(z.string(), z.number().int())
          .describe('Facet counts aggregated per value.')
          .optional(),
      })
    )
    .describe('Facet query result.')
    .optional(),
  rsvpFacets: z
    .object({
      facets: z
        .record(
          z.string(),
          z.object({
            counts: z
              .record(
                z.string(),
                z.object({
                  count: z
                    .number()
                    .int()
                    .describe('Number of RSVPs.')
                    .optional(),
                  guests: z
                    .number()
                    .int()
                    .describe('Number of guests within RSVPs.')
                    .optional(),
                  guestsCheckIn: z
                    .number()
                    .int()
                    .describe('Number of guests who have checked-in.')
                    .optional(),
                })
              )
              .describe('Facet totals, aggregated per filter.')
              .optional(),
          })
        )
        .describe('Filter facets.')
        .optional(),
    })
    .describe('Rsvp data enriched facets.')
    .optional(),
});
export const GetRsvpRequest = z.object({
  rsvpId: z
    .string()
    .describe('RSVP ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      eventId: z
        .string()
        .describe('Event ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      fieldset: z
        .array(z.enum(['DETAILS', 'FORM', 'CONTACT_DETAILS']))
        .max(20)
        .optional(),
    })
    .describe('Optional fields.')
    .optional(),
});
export const GetRsvpResponse = z.object({
  _id: z
    .string()
    .describe('RSVP ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  eventId: z
    .string()
    .describe('Event ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  contactId: z
    .string()
    .describe('Contact ID associated with this RSVP.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  memberId: z
    .string()
    .describe('Member ID associated with this RSVP.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  created: z.date().describe('RSVP created timestamp.').optional().nullable(),
  modified: z.date().describe('RSVP modified timestamp.').optional().nullable(),
  firstName: z.string().describe('First name.').optional(),
  lastName: z.string().describe('Last name.').optional(),
  email: z.string().describe('Guest email.').email().optional(),
  rsvpForm: z
    .object({
      inputValues: z
        .array(
          z.object({
            inputName: z
              .string()
              .describe('Form field input name.')
              .max(100)
              .optional(),
            value: z
              .string()
              .describe('Form field value.')
              .max(5000)
              .optional(),
            values: z.array(z.string()).max(100).optional(),
          })
        )
        .max(200)
        .optional(),
    })
    .describe('RSVP form response.')
    .optional(),
  status: z
    .enum(['YES', 'NO', 'WAITING'])
    .describe('RSVP response status.')
    .optional(),
  totalGuests: z
    .number()
    .int()
    .describe('Total number of attendees.')
    .optional(),
  guests: z
    .array(
      z.object({
        index: z
          .number()
          .int()
          .describe('Index in the RSVP guest list.')
          .optional(),
        fullName: z.string().describe('Guest full name.').optional(),
        checkIn: z
          .object({
            created: z
              .date()
              .describe("Time of a ticket's check-in.")
              .optional()
              .nullable(),
          })
          .describe('Guest check-in.')
          .optional(),
        _id: z
          .number()
          .int()
          .describe('Unique guest ID per RSVP.')
          .min(1)
          .optional(),
      })
    )
    .optional(),
  anonymized: z
    .boolean()
    .describe('Whether RSVP is anonymized by GDPR delete.')
    .optional(),
  marketingConsent: z
    .boolean()
    .describe('Whether marketing consent was given')
    .optional()
    .nullable(),
});
export const CreateRsvpRequest = z.object({
  options: z
    .object({
      eventId: z
        .string()
        .describe('Event ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      form: z
        .object({
          inputValues: z
            .array(
              z.object({
                inputName: z
                  .string()
                  .describe('Form field input name.')
                  .max(100)
                  .optional(),
                value: z
                  .string()
                  .describe('Form field value.')
                  .max(5000)
                  .optional(),
                values: z.array(z.string()).max(100).optional(),
              })
            )
            .max(200)
            .optional(),
        })
        .describe('RSVP form response.')
        .optional(),
      status: z.enum(['YES', 'NO', 'WAITING']).optional(),
      memberId: z
        .string()
        .describe('Member ID of the RSVP.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      options: z
        .object({
          silent: z
            .boolean()
            .describe(
              'Whether to ignore notification settings (when hen true, no notifications to contact or user are sent).'
            )
            .optional(),
          ignoreLimits: z
            .boolean()
            .describe(
              'Whether to create/update regardless of event guest limit.'
            )
            .optional(),
          ignoreFormValidation: z
            .boolean()
            .describe('Whether to ignore the form validation.')
            .optional(),
        })
        .describe(
          'Create RSVP options.\nWIX_EVENTS.MANAGE_RSVP permission is required.'
        )
        .optional(),
      marketingConsent: z
        .boolean()
        .describe('Whether marketing consent was given')
        .optional()
        .nullable(),
    })
    .describe('Optional fields.')
    .optional(),
});
export const CreateRsvpResponse = z.object({
  rsvp: z
    .object({
      _id: z
        .string()
        .describe('RSVP ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      eventId: z
        .string()
        .describe('Event ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      contactId: z
        .string()
        .describe('Contact ID associated with this RSVP.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      memberId: z
        .string()
        .describe('Member ID associated with this RSVP.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      created: z
        .date()
        .describe('RSVP created timestamp.')
        .optional()
        .nullable(),
      modified: z
        .date()
        .describe('RSVP modified timestamp.')
        .optional()
        .nullable(),
      firstName: z.string().describe('First name.').optional(),
      lastName: z.string().describe('Last name.').optional(),
      email: z.string().describe('Guest email.').email().optional(),
      rsvpForm: z
        .object({
          inputValues: z
            .array(
              z.object({
                inputName: z
                  .string()
                  .describe('Form field input name.')
                  .max(100)
                  .optional(),
                value: z
                  .string()
                  .describe('Form field value.')
                  .max(5000)
                  .optional(),
                values: z.array(z.string()).max(100).optional(),
              })
            )
            .max(200)
            .optional(),
        })
        .describe('RSVP form response.')
        .optional(),
      status: z
        .enum(['YES', 'NO', 'WAITING'])
        .describe('RSVP response status.')
        .optional(),
      totalGuests: z
        .number()
        .int()
        .describe('Total number of attendees.')
        .optional(),
      guests: z
        .array(
          z.object({
            index: z
              .number()
              .int()
              .describe('Index in the RSVP guest list.')
              .optional(),
            fullName: z.string().describe('Guest full name.').optional(),
            checkIn: z
              .object({
                created: z
                  .date()
                  .describe("Time of a ticket's check-in.")
                  .optional()
                  .nullable(),
              })
              .describe('Guest check-in.')
              .optional(),
            _id: z
              .number()
              .int()
              .describe('Unique guest ID per RSVP.')
              .min(1)
              .optional(),
          })
        )
        .optional(),
      anonymized: z
        .boolean()
        .describe('Whether RSVP is anonymized by GDPR delete.')
        .optional(),
      marketingConsent: z
        .boolean()
        .describe('Whether marketing consent was given')
        .optional()
        .nullable(),
    })
    .describe('Created RSVP.')
    .optional(),
  calendarLinks: z
    .object({
      google: z.string().describe('"Add to Google calendar" URL.').optional(),
      ics: z.string().describe('"Download ICS calendar file" URL.').optional(),
    })
    .describe('"Add to calendar" links.')
    .optional(),
});
export const UpdateRsvpRequest = z.object({
  rsvpId: z
    .string()
    .describe('RSVP ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  eventId: z
    .string()
    .describe('Event ID to which RSVP belongs.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      fields: z
        .object({ paths: z.array(z.string()) })
        .describe(
          'Set of field paths, specifying which parts of RSVP to update.'
        ),
      rsvpForm: z
        .object({
          inputValues: z
            .array(
              z.object({
                inputName: z
                  .string()
                  .describe('Form field input name.')
                  .max(100)
                  .optional(),
                value: z
                  .string()
                  .describe('Form field value.')
                  .max(5000)
                  .optional(),
                values: z.array(z.string()).max(100).optional(),
              })
            )
            .max(200)
            .optional(),
        })
        .describe('RSVP form response.')
        .optional(),
      status: z.enum(['YES', 'NO', 'WAITING']).optional(),
      options: z
        .object({
          silent: z
            .boolean()
            .describe(
              'Whether to ignore notification settings (when hen true, no notifications to contact or user are sent).'
            )
            .optional(),
          ignoreLimits: z
            .boolean()
            .describe(
              'Whether to create/update regardless of event guest limit.'
            )
            .optional(),
          ignoreFormValidation: z
            .boolean()
            .describe('Whether to ignore the form validation.')
            .optional(),
        })
        .describe(
          'Update RSVP options.\nWIX_EVENTS.MANAGE_RSVP permission is required.'
        )
        .optional(),
    })
    .describe('Optional fields.'),
});
export const UpdateRsvpResponse = z.object({
  rsvp: z
    .object({
      _id: z
        .string()
        .describe('RSVP ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      eventId: z
        .string()
        .describe('Event ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      contactId: z
        .string()
        .describe('Contact ID associated with this RSVP.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      memberId: z
        .string()
        .describe('Member ID associated with this RSVP.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      created: z
        .date()
        .describe('RSVP created timestamp.')
        .optional()
        .nullable(),
      modified: z
        .date()
        .describe('RSVP modified timestamp.')
        .optional()
        .nullable(),
      firstName: z.string().describe('First name.').optional(),
      lastName: z.string().describe('Last name.').optional(),
      email: z.string().describe('Guest email.').email().optional(),
      rsvpForm: z
        .object({
          inputValues: z
            .array(
              z.object({
                inputName: z
                  .string()
                  .describe('Form field input name.')
                  .max(100)
                  .optional(),
                value: z
                  .string()
                  .describe('Form field value.')
                  .max(5000)
                  .optional(),
                values: z.array(z.string()).max(100).optional(),
              })
            )
            .max(200)
            .optional(),
        })
        .describe('RSVP form response.')
        .optional(),
      status: z
        .enum(['YES', 'NO', 'WAITING'])
        .describe('RSVP response status.')
        .optional(),
      totalGuests: z
        .number()
        .int()
        .describe('Total number of attendees.')
        .optional(),
      guests: z
        .array(
          z.object({
            index: z
              .number()
              .int()
              .describe('Index in the RSVP guest list.')
              .optional(),
            fullName: z.string().describe('Guest full name.').optional(),
            checkIn: z
              .object({
                created: z
                  .date()
                  .describe("Time of a ticket's check-in.")
                  .optional()
                  .nullable(),
              })
              .describe('Guest check-in.')
              .optional(),
            _id: z
              .number()
              .int()
              .describe('Unique guest ID per RSVP.')
              .min(1)
              .optional(),
          })
        )
        .optional(),
      anonymized: z
        .boolean()
        .describe('Whether RSVP is anonymized by GDPR delete.')
        .optional(),
      marketingConsent: z
        .boolean()
        .describe('Whether marketing consent was given')
        .optional()
        .nullable(),
    })
    .describe('Updated RSVP.')
    .optional(),
});
export const BulkUpdateRsvpRequest = z.object({
  eventId: z
    .string()
    .describe('Event ID to which RSVP belongs.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      rsvpId: z.array(z.string()).min(1).max(100).optional(),
      fields: z
        .object({ paths: z.array(z.string()) })
        .describe('Set of fields to update.')
        .optional()
        .nullable(),
      status: z.enum(['YES', 'NO', 'WAITING']).optional(),
    })
    .describe('Optional fields.')
    .optional(),
});
export const BulkUpdateRsvpResponse = z.object({
  rsvps: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('RSVP ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        eventId: z
          .string()
          .describe('Event ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        contactId: z
          .string()
          .describe('Contact ID associated with this RSVP.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        memberId: z
          .string()
          .describe('Member ID associated with this RSVP.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        created: z
          .date()
          .describe('RSVP created timestamp.')
          .optional()
          .nullable(),
        modified: z
          .date()
          .describe('RSVP modified timestamp.')
          .optional()
          .nullable(),
        firstName: z.string().describe('First name.').optional(),
        lastName: z.string().describe('Last name.').optional(),
        email: z.string().describe('Guest email.').email().optional(),
        rsvpForm: z
          .object({
            inputValues: z
              .array(
                z.object({
                  inputName: z
                    .string()
                    .describe('Form field input name.')
                    .max(100)
                    .optional(),
                  value: z
                    .string()
                    .describe('Form field value.')
                    .max(5000)
                    .optional(),
                  values: z.array(z.string()).max(100).optional(),
                })
              )
              .max(200)
              .optional(),
          })
          .describe('RSVP form response.')
          .optional(),
        status: z
          .enum(['YES', 'NO', 'WAITING'])
          .describe('RSVP response status.')
          .optional(),
        totalGuests: z
          .number()
          .int()
          .describe('Total number of attendees.')
          .optional(),
        guests: z
          .array(
            z.object({
              index: z
                .number()
                .int()
                .describe('Index in the RSVP guest list.')
                .optional(),
              fullName: z.string().describe('Guest full name.').optional(),
              checkIn: z
                .object({
                  created: z
                    .date()
                    .describe("Time of a ticket's check-in.")
                    .optional()
                    .nullable(),
                })
                .describe('Guest check-in.')
                .optional(),
              _id: z
                .number()
                .int()
                .describe('Unique guest ID per RSVP.')
                .min(1)
                .optional(),
            })
          )
          .optional(),
        anonymized: z
          .boolean()
          .describe('Whether RSVP is anonymized by GDPR delete.')
          .optional(),
        marketingConsent: z
          .boolean()
          .describe('Whether marketing consent was given')
          .optional()
          .nullable(),
      })
    )
    .optional(),
});
export const DeleteRsvpRequest = z.object({
  eventId: z
    .string()
    .describe('Event ID to which RSVP belongs.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({ rsvps: z.array(z.string()).min(1).max(100).optional() })
    .describe('Optional fields.')
    .optional(),
});
export const DeleteRsvpResponse = z.object({});
export const CheckInRsvpRequest = z.object({
  eventId: z
    .string()
    .describe('Event ID to which RSVP belongs.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      rsvpId: z
        .string()
        .describe('RSVP ID to check-in.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        ),
      guestId: z.array(z.number().int()).min(1).max(11).optional(),
    })
    .describe('Optional fields.'),
});
export const CheckInRsvpResponse = z.object({
  rsvp: z
    .object({
      _id: z
        .string()
        .describe('RSVP ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      eventId: z
        .string()
        .describe('Event ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      contactId: z
        .string()
        .describe('Contact ID associated with this RSVP.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      memberId: z
        .string()
        .describe('Member ID associated with this RSVP.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      created: z
        .date()
        .describe('RSVP created timestamp.')
        .optional()
        .nullable(),
      modified: z
        .date()
        .describe('RSVP modified timestamp.')
        .optional()
        .nullable(),
      firstName: z.string().describe('First name.').optional(),
      lastName: z.string().describe('Last name.').optional(),
      email: z.string().describe('Guest email.').email().optional(),
      rsvpForm: z
        .object({
          inputValues: z
            .array(
              z.object({
                inputName: z
                  .string()
                  .describe('Form field input name.')
                  .max(100)
                  .optional(),
                value: z
                  .string()
                  .describe('Form field value.')
                  .max(5000)
                  .optional(),
                values: z.array(z.string()).max(100).optional(),
              })
            )
            .max(200)
            .optional(),
        })
        .describe('RSVP form response.')
        .optional(),
      status: z
        .enum(['YES', 'NO', 'WAITING'])
        .describe('RSVP response status.')
        .optional(),
      totalGuests: z
        .number()
        .int()
        .describe('Total number of attendees.')
        .optional(),
      guests: z
        .array(
          z.object({
            index: z
              .number()
              .int()
              .describe('Index in the RSVP guest list.')
              .optional(),
            fullName: z.string().describe('Guest full name.').optional(),
            checkIn: z
              .object({
                created: z
                  .date()
                  .describe("Time of a ticket's check-in.")
                  .optional()
                  .nullable(),
              })
              .describe('Guest check-in.')
              .optional(),
            _id: z
              .number()
              .int()
              .describe('Unique guest ID per RSVP.')
              .min(1)
              .optional(),
          })
        )
        .optional(),
      anonymized: z
        .boolean()
        .describe('Whether RSVP is anonymized by GDPR delete.')
        .optional(),
      marketingConsent: z
        .boolean()
        .describe('Whether marketing consent was given')
        .optional()
        .nullable(),
    })
    .describe('Updated RSVP.')
    .optional(),
});
export const DeleteRsvpCheckInRequest = z.object({
  eventId: z
    .string()
    .describe('Event ID to which RSVP belongs.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      rsvpId: z
        .string()
        .describe('RSVP ID to delete check-in.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        ),
      guestId: z.array(z.number().int()).min(1).max(11).optional(),
    })
    .describe('Optional fields.'),
});
export const DeleteRsvpCheckInResponse = z.object({
  rsvp: z
    .object({
      _id: z
        .string()
        .describe('RSVP ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      eventId: z
        .string()
        .describe('Event ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      contactId: z
        .string()
        .describe('Contact ID associated with this RSVP.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      memberId: z
        .string()
        .describe('Member ID associated with this RSVP.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      created: z
        .date()
        .describe('RSVP created timestamp.')
        .optional()
        .nullable(),
      modified: z
        .date()
        .describe('RSVP modified timestamp.')
        .optional()
        .nullable(),
      firstName: z.string().describe('First name.').optional(),
      lastName: z.string().describe('Last name.').optional(),
      email: z.string().describe('Guest email.').email().optional(),
      rsvpForm: z
        .object({
          inputValues: z
            .array(
              z.object({
                inputName: z
                  .string()
                  .describe('Form field input name.')
                  .max(100)
                  .optional(),
                value: z
                  .string()
                  .describe('Form field value.')
                  .max(5000)
                  .optional(),
                values: z.array(z.string()).max(100).optional(),
              })
            )
            .max(200)
            .optional(),
        })
        .describe('RSVP form response.')
        .optional(),
      status: z
        .enum(['YES', 'NO', 'WAITING'])
        .describe('RSVP response status.')
        .optional(),
      totalGuests: z
        .number()
        .int()
        .describe('Total number of attendees.')
        .optional(),
      guests: z
        .array(
          z.object({
            index: z
              .number()
              .int()
              .describe('Index in the RSVP guest list.')
              .optional(),
            fullName: z.string().describe('Guest full name.').optional(),
            checkIn: z
              .object({
                created: z
                  .date()
                  .describe("Time of a ticket's check-in.")
                  .optional()
                  .nullable(),
              })
              .describe('Guest check-in.')
              .optional(),
            _id: z
              .number()
              .int()
              .describe('Unique guest ID per RSVP.')
              .min(1)
              .optional(),
          })
        )
        .optional(),
      anonymized: z
        .boolean()
        .describe('Whether RSVP is anonymized by GDPR delete.')
        .optional(),
      marketingConsent: z
        .boolean()
        .describe('Whether marketing consent was given')
        .optional()
        .nullable(),
    })
    .describe('Updated RSVP.')
    .optional(),
});
